import { faCloudUploadAlt } from "@fortawesome/pro-solid-svg-icons/faCloudUploadAlt";
import { faCloudXmark } from "@fortawesome/pro-solid-svg-icons/faCloudXmark";
import { Alert, AlertTitle, Box, Stack, SxProps, Theme, useFormControl } from "@mui/material";
import { FaIcon } from "@shuttlerock/mui-components";
import { createContext, InputHTMLAttributes, PropsWithChildren, useContext, useMemo } from "react";
import {
  DropEvent,
  DropzoneRootProps,
  DropzoneState,
  FileRejection,
  useDropzone,
} from "react-dropzone";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { useTranslation } from "../../i18n";
import { toDropzoneAccept } from "../../utils/toDropzoneAccept";

export type DropzoneInputProps = {
  name: string;
  sx?: SxProps<Theme>;
  error?: boolean;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "name">;

export const dropzoneInputClasses = {
  root: "DropzoneInput-root",
  error: "Mui-error",
  dragActive: "DropzoneInput-dragActive",
  dragAccept: "DropzoneInput-dragAccept",
  dragReject: "DropzoneInput-dragReject",
  placeholder: "DropzoneInput-placeholder",
  placeholderIcon: "DropzoneInput-placeholderIcon",
} as const;

const inputSx: SxProps<Theme> = {
  py: 3.5,
  borderRadius: 1,
  "--outline-color": "transparent",
  position: "relative",
  backgroundColor: "background.card",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
  input: { display: "none" },
  transition: (theme) => theme.transitions.create("background"),
  [`&:after`]: {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "inherit",
    boxShadow: "inset 0 0 0 1px var(--outline-color)",
    pointerEvents: "none",
  },
  [`.${dropzoneInputClasses.placeholder}`]: {
    display: "flex",
    alignItems: "center",
    color: "text.primaryAlt",
    gap: 1,
    fontWeight: 600,
    fontSize: (theme) => theme.typography.pxToRem(16),
    lineHeight: (theme) => theme.typography.pxToRem(26),
    transition: (theme) => theme.transitions.create("color"),
  },
  [`&.${dropzoneInputClasses.dragActive}`]: {
    backgroundColor: (theme) =>
      `color-mix(in srgb, ${theme.vars.palette.background.card} 10%, transparent)`,
    [`.${dropzoneInputClasses.placeholder}`]: {
      color: "primary.main",
    },
  },
  [`&.${dropzoneInputClasses.dragReject}`]: {
    [`.${dropzoneInputClasses.placeholderIcon}`]: {
      color: "warning.main",
    },
  },
  [`&.${dropzoneInputClasses.error}:not(.${dropzoneInputClasses.dragActive})`]: {
    "--outline-color": (theme) => theme.vars.palette.warning.main,
  },
};

const DropzoneInputImpl = ({
  name,
  sx,
  children,
  id: idIn,
  error: errorProp,
  ...inputProps
}: PropsWithChildren<DropzoneInputProps>) => {
  const dropzoneState = useDropzoneContext();
  if (!dropzoneState) {
    throw new DropzoneCompositionError("DropzoneInput");
  }

  const { t } = useTranslation();
  const formControl = useFormControl();
  const id = idIn || name;
  const error = errorProp ?? formControl?.error;
  const {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    ...restInputProps
  } = inputProps;
  const { open, isDragActive, isDragAccept, isDragReject, getInputProps } = dropzoneState;
  const className = [
    dropzoneInputClasses.root,
    error && dropzoneInputClasses.error,
    isDragActive && dropzoneInputClasses.dragActive,
    isDragAccept && dropzoneInputClasses.dragAccept,
    isDragReject && dropzoneInputClasses.dragReject,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Stack
      className={className}
      id={`${id}-root`}
      data-testid={`${id}-root`}
      sx={mergeSx(inputSx, sx)}
      onClick={open}
      onKeyDown={(event) => {
        // Keys pressed on interactive children are theirs.
        if (event.target !== event.currentTarget) return;
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        open();
      }}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      <input
        {...getInputProps({
          ...restInputProps,
          id,
          name,
          "aria-label": ariaLabel,
          "aria-labelledby": ariaLabelledBy,
          "data-testid": `${id}-input`,
        })}
      />
      <div className={dropzoneInputClasses.placeholder}>
        <FaIcon
          icon={isDragReject ? faCloudXmark : faCloudUploadAlt}
          className={dropzoneInputClasses.placeholderIcon}
        />
        {t("dropzone:placeholder")}
      </div>
      {children}
    </Stack>
  );
};

const DropzoneInputErrorBoundary = ({ error }: FallbackProps) => {
  if (error instanceof DropzoneCompositionError) {
    throw error;
  }

  const { t } = useTranslation();

  return (
    <Alert variant="standard" severity="info" sx={{ my: 2 }}>
      <AlertTitle>{t("dropzone:errorBoundary:title")}</AlertTitle>
      {t("dropzone:errorBoundary:message")}
    </Alert>
  );
};

export const DropzoneInput = (props: DropzoneInputProps) => {
  return (
    <ErrorBoundary FallbackComponent={DropzoneInputErrorBoundary} onError={console.error}>
      <DropzoneInputImpl {...props} />
    </ErrorBoundary>
  );
};

class DropzoneCompositionError extends Error {
  constructor(component: string) {
    super(`${component} must be used within a DropzoneArea`);
  }
}

export type DropzoneContext = Omit<DropzoneState, "getRootProps">;
const Context = createContext<DropzoneContext | undefined>(undefined);

export function useDropzoneContext() {
  return useContext(Context);
}

export type { DropEvent, FileRejection } from "react-dropzone";

export type DropzoneAreaProps = {
  /**
   * File extensions (`.png`) and/or MIME types (`image/*`) accepted by the dropzone,
   * converted internally to react-dropzone's MIME-keyed `accept` object.
   * @example ['.jpg', '.jpeg', '.png', 'image/*']
   */
  accept?: string[];
  /**
   * Allow drag 'n' drop (or selection from the file dialog) of multiple files
   * @default true
   */
  multiple?: boolean;
  /**
   * Enable/disable the dropzone
   * @default false
   */
  disabled?: boolean;
  sx?: SxProps<Theme>;
  onChange?<T extends File>(files: T[], event: DropEvent): void;
  onReject?(fileRejections: FileRejection[], event: DropEvent): void;
};

export const dropzoneAreaClasses = {
  root: "DropzoneArea-root",
  dragActive: "DropzoneArea-dragActive",
  dragAccept: "DropzoneArea-dragAccept",
  dragReject: "DropzoneArea-dragReject",
  hideOnDragActive: "DropzoneArea-hideOnDragActive",
} as const;

const areaSx: SxProps<Theme> = {
  borderRadius: 1,
  "--outline-color": "transparent",
  position: "relative",
  [`&:after`]: {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "inherit",
    boxShadow: "inset 0 0 0 1px var(--outline-color)",
    pointerEvents: "none",
  },
  transition: (theme) => theme.transitions.create("box-shadow"),
  [`.${dropzoneAreaClasses.hideOnDragActive}`]: {
    transition: (theme) => theme.transitions.create("opacity"),
  },
  [`&.${dropzoneAreaClasses.dragActive}`]: {
    [`.${dropzoneAreaClasses.hideOnDragActive}`]: {
      opacity: 0.1,
    },
  },
  [`&.${dropzoneAreaClasses.dragAccept}`]: (theme) => ({
    "--outline-color": theme.palette.primary.main,
  }),
  [`&.${dropzoneAreaClasses.dragReject}`]: (theme) => ({
    "--outline-color": theme.palette.error.main,
  }),
};

export const DropzoneArea = ({
  accept,
  multiple = true,
  disabled: disabledProp = false,
  sx: sxProp,
  onChange: onDropAccepted,
  onReject: onDropRejected,
  children,
}: PropsWithChildren<DropzoneAreaProps>) => {
  const formControl = useFormControl();
  const disabled = disabledProp ?? formControl?.disabled;

  const { getRootProps, isDragReject, ...dropzoneState } = useDropzone({
    accept: accept ? toDropzoneAccept(accept) : undefined,
    multiple,
    disabled,
    onDropAccepted,
    onDropRejected,
    noClick: true,
    noKeyboard: true,
  });

  // Due to browser limitations `isDragReject` will erroneously return `true` when using file extensions to limit
  // the accepted file types. For this to work correctly we need to use MIME types instead.
  // See https://react-dropzone.js.org/#browser-limitations
  const isUsingFileExtensions = accept?.some((a) => a.includes("."));
  const effectiveDragReject = !isUsingFileExtensions && isDragReject;

  const { isDragActive, isDragAccept } = dropzoneState;
  const className = [
    dropzoneAreaClasses.root,
    isDragActive && dropzoneAreaClasses.dragActive,
    (isDragAccept || (isDragActive && !effectiveDragReject)) && dropzoneAreaClasses.dragAccept,
    effectiveDragReject && dropzoneAreaClasses.dragReject,
  ]
    .filter(Boolean)
    .join(" ");

  const value = useMemo(
    () => ({
      ...dropzoneState,
      isDragReject: effectiveDragReject,
    }),
    [dropzoneState, effectiveDragReject],
  );

  // react-dropzone labels its root `role="presentation"`; the area is a plain container.
  const { role: _presentation, ...rootProps } = getRootProps<DropzoneRootProps>({
    className,
    sx: mergeSx(areaSx, sxProp),
  });

  return (
    <Context.Provider value={value}>
      <Box {...rootProps}>{children}</Box>
    </Context.Provider>
  );
};

type SxPropsWithoutArray = Exclude<SxProps<Theme>, ReadonlyArray<unknown>>;

function mergeSx(base: SxPropsWithoutArray, sxProp: SxProps<Theme> | undefined): SxProps<Theme> {
  return sxProp ? [base, ...(Array.isArray(sxProp) ? sxProp : [sxProp])] : base;
}
